import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useTour } from "@modules/GuidedTour/useTour";
import { getTourSteps } from "@modules/GuidedTour/tourConfigs";
import { useHistory, useLocation } from "react-router-dom";

// Constants
const ROUTES = {
  HOME: "/",
  DASHBOARD: "/dashboard",
  SEARCH: "/search",
  LITERATURE: "/literature",
};

const SELECTORS = {
  HOME_SEARCH_BAR: '[data-tour="home-search-bar"]',
  SEARCH_CONTROLS: '[data-tour="search-controls"]',
  SEARCH_FILTERS: '[data-tour="search-filters"]',
  BOOK_DETAILS: '[data-tour="book-details"]',
  LOAN_BUTTON: '[data-tour="loan-request-button"]',
  FEATURED_BOOK:
    '[data-tour="featured-book"] a, .featured-book a, .book-item a, .book-card a',
  LITERATURE_LINKS: 'a[href*="/literature/"]',
};

const TIMEOUTS = {
  ELEMENT_WAIT: 8000,
  NAVIGATION: 1500,
  TAB_CLICK: 1200,
  RETRY: 1500,
  ROUTE_UPDATE: 800,
  ELEMENT_CHECK: 100,
  PROGRESS_UPDATE: 300,
};

const TOUR_STYLES = {
  tooltip: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "8px",
    padding: "20px",
    maxWidth: "400px",
    minWidth: "280px",
    boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
  },
  title: {
    margin: "0 0 8px 0",
    fontSize: "18px",
    fontWeight: "bold",
    color: "#2185d0",
  },
  progress: {
    fontSize: "12px",
    color: "#666",
    fontWeight: "500",
  },
  content: {
    marginBottom: "16px",
    lineHeight: "1.6",
    color: "#444",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
  },
  buttonGroup: {
    display: "flex",
    gap: "8px",
  },
  prevButton: {
    backgroundColor: "transparent",
    border: "1px solid #767676",
    borderRadius: "4px",
    color: "#767676",
    fontSize: "14px",
    padding: "8px 16px",
    cursor: "pointer",
    minWidth: "80px",
  },
  skipButton: {
    backgroundColor: "transparent",
    border: "none",
    color: "#767676",
    fontSize: "13px",
    cursor: "pointer",
    padding: "8px 12px",
  },
  nextButton: {
    backgroundColor: "#21ba45",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "bold",
    padding: "10px 20px",
    border: "none",
    color: "white",
    cursor: "pointer",
    minWidth: "80px",
  },
  progressBar: {
    marginTop: "16px",
    height: "3px",
    backgroundColor: "#f0f0f0",
    borderRadius: "2px",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#21ba45",
    transition: "width 0.3s ease",
  },
};

const GuidedTour = () => {
  const { activeTour, completeTour, stopTour } = useTour();
  const history = useHistory();
  const location = useLocation();

  const tourDataRef = useRef({
    globalStepIndex: 0,
    allSteps: [],
    isNavigating: false,
    pendingNavigation: null,
  });

  const [tourState, setTourState] = useState({
    run: false,
    stepIndex: 0,
    steps: [],
    currentRoute: "",
  });

  const doesStepMatchRoute = (step, currentRoute) => {
    if (!step.route) return true;

    if (step.route === currentRoute) return true;

    if (
      step.route === ROUTES.HOME &&
      (currentRoute === ROUTES.HOME ||
        currentRoute === "" ||
        currentRoute === ROUTES.DASHBOARD ||
        currentRoute.startsWith("/?"))
    ) {
      return true;
    }

    if (step.route.includes("*")) {
      const baseRoute = step.route.replace("/*", "");
      return currentRoute.startsWith(baseRoute);
    }

    if (
      (step.route === ROUTES.SEARCH || step.route === "/search/*") &&
      (currentRoute === ROUTES.SEARCH || currentRoute.startsWith(ROUTES.SEARCH))
    ) {
      return true;
    }

    if (
      (step.route === "/literature/*" ||
        step.route.startsWith(ROUTES.LITERATURE)) &&
      currentRoute.startsWith(ROUTES.LITERATURE)
    ) {
      return true;
    }

    return false;
  };

  const waitForElement = (selector, timeout = TIMEOUTS.ELEMENT_WAIT) => {
    return new Promise((resolve) => {
      const startTime = Date.now();

      const checkElement = () => {
        const element = document.querySelector(selector);

        if (element && element.offsetParent !== null) {
          resolve(element);
        } else if (Date.now() - startTime > timeout) {
          resolve(null);
        } else {
          setTimeout(checkElement, TIMEOUTS.ELEMENT_CHECK);
        }
      };

      checkElement();
    });
  };

  const handleNavigation = async (step) => {
    if (!step.action || tourDataRef.current.isNavigating) return true;

    tourDataRef.current.isNavigating = true;

    try {
      if (step.action.type === "navigate") {
        if (
          step.action.url.includes("/literature/") &&
          step.action.url.includes("dynamic")
        ) {
          const documentLinks = document.querySelectorAll(
            SELECTORS.LITERATURE_LINKS
          );
          const featuredBookLinks = document.querySelectorAll(
            SELECTORS.FEATURED_BOOK
          );

          let targetUrl = null;

          if (featuredBookLinks.length > 0) {
            const href = featuredBookLinks[0].getAttribute("href");
            if (href && href.includes("/literature/")) {
              targetUrl = href;
            }
          }

          if (!targetUrl && documentLinks.length > 0) {
            const href = documentLinks[0].getAttribute("href");
            if (href && href.includes("/literature/")) {
              targetUrl = href;
            }
          }

          if (!targetUrl) {
            targetUrl = ROUTES.SEARCH;
          }

          tourDataRef.current.pendingNavigation = targetUrl;
          history.push(targetUrl);
        } else {
          tourDataRef.current.pendingNavigation = step.action.url;
          history.push(step.action.url);
        }

        await new Promise((resolve) =>
          setTimeout(resolve, TIMEOUTS.NAVIGATION)
        );

        if (step.action.waitFor) {
          await waitForElement(step.action.waitFor, 12000);
        }
      } else if (step.action.type === "click_tab") {
        const tabElement = document.querySelector(step.action.selector);
        if (tabElement) {
          tabElement.click();

          if (step.action.waitFor) {
            await waitForElement(step.action.waitFor, 3000);
          }

          if (step.action.waitForContent) {
            await waitForElement(step.action.waitForContent, 5000);
          }

          await new Promise((resolve) =>
            setTimeout(resolve, TIMEOUTS.TAB_CLICK)
          );
        }
      } else if (step.action.type === "click_element") {
        const clickableElement = document.querySelector(step.action.selector);
        if (clickableElement) {
          clickableElement.click();
          await new Promise((resolve) =>
            setTimeout(resolve, TIMEOUTS.NAVIGATION)
          );

          if (step.action.waitFor) {
            await waitForElement(step.action.waitFor, TIMEOUTS.ELEMENT_WAIT);
          }
        } else {
          history.push(ROUTES.SEARCH);
        }
      }

      tourDataRef.current.isNavigating = false;
      return true;
    } catch (error) {
      tourDataRef.current.isNavigating = false;
      return true;
    }
  };

  const getStepsForCurrentRoute = () => {
    const currentRoute = location.pathname;
    const allSteps = tourDataRef.current.allSteps;
    const globalIndex = tourDataRef.current.globalStepIndex;

    const currentStep = allSteps[globalIndex];
    if (!currentStep) {
      return { steps: [], localIndex: 0 };
    }

    const stepMatches = doesStepMatchRoute(currentStep, currentRoute);

    if (stepMatches) {
      return {
        steps: allSteps,
        localIndex: globalIndex,
      };
    } else {
      return { steps: [], localIndex: 0 };
    }
  };

  const handleJoyrideCallback = async (data) => {
    const { action, status, type } = data;

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      if (action === ACTIONS.NEXT) {
        const currentGlobalStep =
          tourDataRef.current.allSteps[tourDataRef.current.globalStepIndex];

        if (currentGlobalStep?.action) {
          await handleNavigation(currentGlobalStep);
        }

        tourDataRef.current.globalStepIndex += 1;

        if (
          tourDataRef.current.globalStepIndex >=
          tourDataRef.current.allSteps.length
        ) {
          setTourState((prev) => ({ ...prev, run: false }));
          completeTour(activeTour);
          return;
        }

        setTimeout(() => {
          updateTourStateForRoute();
        }, 500);
      } else if (action === ACTIONS.PREV) {
        const currentGlobalStep =
          tourDataRef.current.allSteps[tourDataRef.current.globalStepIndex];

        if (currentGlobalStep?.hidePrev) {
          return;
        }

        const newIndex = Math.max(0, tourDataRef.current.globalStepIndex - 1);
        tourDataRef.current.globalStepIndex = newIndex;

        setTimeout(() => {
          updateTourStateForRoute();
        }, 300);
      }
    } else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setTourState((prev) => ({ ...prev, run: false }));

      if (status === STATUS.FINISHED) {
        completeTour(activeTour);
      } else {
        stopTour();
      }
    }
  };

  const updateTourStateForRoute = () => {
    const { steps, localIndex } = getStepsForCurrentRoute();

    if (steps.length > 0) {
      setTourState((prev) => ({
        ...prev,
        steps,
        stepIndex: localIndex,
        run: true,
        currentRoute: location.pathname,
      }));
    } else {
      setTourState((prev) => ({
        ...prev,
        run: false,
        currentRoute: location.pathname,
      }));

      setTimeout(() => {
        if (
          tourDataRef.current.globalStepIndex <
          tourDataRef.current.allSteps.length
        ) {
          const { steps: retrySteps, localIndex: retryIndex } =
            getStepsForCurrentRoute();

          if (retrySteps.length > 0) {
            setTourState((prev) => ({
              ...prev,
              steps: retrySteps,
              stepIndex: retryIndex,
              run: true,
              currentRoute: location.pathname,
            }));
          }
        }
      }, TIMEOUTS.RETRY);
    }
  };

  const waitForRouteElements = (elementSelectors, callback) => {
    const checkElements = () => {
      const elements = elementSelectors.map((selector) =>
        document.querySelector(selector)
      );

      if (elements.some((el) => el)) {
        window.scrollTo({ top: 0, behavior: "auto" });
        setTimeout(callback, TIMEOUTS.ROUTE_UPDATE);
      } else {
        setTimeout(() => {
          const retryElements = elementSelectors.map((selector) =>
            document.querySelector(selector)
          );
          if (retryElements.some((el) => el)) {
            window.scrollTo({ top: 0, behavior: "auto" });
            callback();
          } else {
            callback();
          }
        }, TIMEOUTS.RETRY);
      }
    };

    checkElements();
  };

  const customTooltip = ({
    step,
    tooltipProps,
    primaryProps,
    backProps,
    skipProps,
  }) => {
    const currentGlobalIndex = tourDataRef.current.globalStepIndex;
    const totalSteps = tourDataRef.current.allSteps.length;
    const shouldHidePrev = step.hidePrev || currentGlobalIndex === 0;

    return (
      <div
        {...tooltipProps}
        style={{ ...tooltipProps.style, ...TOUR_STYLES.tooltip }}
      >
        <div style={{ marginBottom: "12px" }}>
          {step.title && <h4 style={TOUR_STYLES.title}>{step.title}</h4>}
          <div style={TOUR_STYLES.progress}>
            Step {currentGlobalIndex + 1} of {totalSteps}
          </div>
        </div>

        <div style={TOUR_STYLES.content}>{step.content}</div>

        <div style={TOUR_STYLES.buttonContainer}>
          <div>
            {!shouldHidePrev && (
              <button {...backProps} style={TOUR_STYLES.prevButton}>
                ← Previous
              </button>
            )}
          </div>

          <div style={TOUR_STYLES.buttonGroup}>
            <button {...skipProps} style={TOUR_STYLES.skipButton}>
              Skip Tour
            </button>
            <button {...primaryProps} style={TOUR_STYLES.nextButton}>
              {currentGlobalIndex + 1 === totalSteps ? "Finish Tour" : "Next →"}
            </button>
          </div>
        </div>

        <div style={TOUR_STYLES.progressBar}>
          <div
            style={{
              ...TOUR_STYLES.progressFill,
              width: `${((currentGlobalIndex + 1) / totalSteps) * 100}%`,
            }}
          />
        </div>
      </div>
    );
  };

  customTooltip.propTypes = {
    step: PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      hidePrev: PropTypes.bool,
    }).isRequired,
    tooltipProps: PropTypes.shape({
      style: PropTypes.object,
    }).isRequired,
    primaryProps: PropTypes.object.isRequired,
    backProps: PropTypes.object.isRequired,
    skipProps: PropTypes.object.isRequired,
  };

  useEffect(() => {
    if (activeTour) {
      const allSteps = getTourSteps(activeTour);

      tourDataRef.current = {
        globalStepIndex: 0,
        allSteps,
        isNavigating: false,
        pendingNavigation: null,
      };

      window.scrollTo({ top: 0, behavior: "smooth" });

      setTimeout(() => {
        updateTourStateForRoute();
      }, TIMEOUTS.ROUTE_UPDATE);
    } else {
      setTourState((prev) => ({ ...prev, run: false }));
    }
  }, [activeTour]);

  useEffect(() => {
    if (activeTour && tourDataRef.current.allSteps.length > 0) {
      if (tourDataRef.current.pendingNavigation === location.pathname) {
        tourDataRef.current.pendingNavigation = null;
      }

      if (location.pathname === ROUTES.HOME || location.pathname === "") {
        waitForRouteElements(
          [SELECTORS.HOME_SEARCH_BAR],
          updateTourStateForRoute
        );
        return;
      }

      if (
        location.pathname === ROUTES.SEARCH ||
        location.pathname.startsWith(ROUTES.SEARCH)
      ) {
        waitForRouteElements(
          [SELECTORS.SEARCH_CONTROLS, SELECTORS.SEARCH_FILTERS],
          updateTourStateForRoute
        );
        return;
      }

      if (location.pathname.startsWith(ROUTES.LITERATURE)) {
        waitForRouteElements(
          [SELECTORS.BOOK_DETAILS, SELECTORS.LOAN_BUTTON],
          updateTourStateForRoute
        );
        return;
      }

      window.scrollTo({ top: 0, behavior: "auto" });
      setTimeout(() => {
        updateTourStateForRoute();
      }, 1000);
    }
  }, [location.pathname, activeTour]);

  // Conditional rendering instead of early return
  const shouldRenderTour = activeTour && tourState.run;

  return (
    <>
      {shouldRenderTour && (
        <>
          <style>
            {`
              .react-joyride__beacon {
                display: none !important;
              }
              .react-joyride__spotlight {
                pointer-events: none !important;
              }
              .react-joyride__tooltip {
                box-shadow: 0 6px 20px rgba(0,0,0,0.25) !important;
              }
            `}
          </style>

          <Joyride
            callback={handleJoyrideCallback}
            continuous
            run={tourState.run && !tourDataRef.current.isNavigating}
            stepIndex={tourState.stepIndex}
            steps={tourState.steps}
            showProgress={false}
            showSkipButton={false}
            disableOverlayClose
            hideCloseButton
            disableScrolling={false}
            scrollToFirstStep
            scrollOffset={30}
            scrollDuration={500}
            disableBeacon
            spotlightClicks={false}
            tooltipComponent={customTooltip}
            styles={{
              options: {
                primaryColor: "#21ba45",
                textColor: "#333",
                backgroundColor: "#fff",
                overlayColor: "rgba(0, 0, 0, 0.7)",
                spotlightShadow: "0 0 30px rgba(0, 0, 0, 0.8)",
                zIndex: 10000,
                arrowColor: "#fff",
              },
              spotlight: {
                borderRadius: 8,
                border: "4px solid #21ba45",
              },
            }}
            locale={{
              back: "← Previous",
              close: "Close",
              last: "Finish Tour",
              next: "Next →",
              skip: "Skip Tour",
            }}
            floaterProps={{
              disableAnimation: false,
              hideArrow: false,
              offset: 15,
              placement: "auto",
              styles: {
                floater: {
                  filter: "drop-shadow(0 6px 20px rgba(0,0,0,0.25))",
                },
              },
            }}
          />
        </>
      )}
    </>
  );
};

export default GuidedTour;
